import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import { Link } from '@/components/link';
import { productCardHeadingLinkClass, productCardHeadingLinkFocusRingClass } from '@/lib/focus-styles';
import { cn } from '@/lib/utils';
import { ProductTileCustomVerticalImageSection } from '@/components/product-tile-custom/product-tile-custom-vertical-image-section';
import type { ShopperSearch } from '@/scapi';

type SearchHit = ShopperSearch.schemas['ProductSearchHit'];

export interface ProductTileCustomVerticalEventCardProps extends ComponentProps<'div'> {
    product: SearchHit;
    productName: string;
    productUrl: string;
    effectiveImgAspectRatio: number;
    formattedDateLine?: string;
    categoryName?: string;
    priceBlock: ReactNode;
    handleProductClick?: (product: SearchHit) => void;
}

/**
 * Figma card-product (1:22943): course/event vertical tile with date, title, price, and category.
 */
function ProductTileCustomVerticalEventCardInner(
    {
        product,
        productName,
        productUrl,
        effectiveImgAspectRatio,
        formattedDateLine,
        categoryName,
        priceBlock,
        handleProductClick,
        className,
        ...rest
    }: ProductTileCustomVerticalEventCardProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    return (
        <div
            ref={ref}
            data-testid="product-tile"
            data-tile-variant="vertical-event"
            className={cn(
                'group/product-card relative h-full w-full min-w-0 max-w-full cursor-default',
                'flex flex-col gap-4 rounded-3xl bg-sand-95 text-card-foreground overflow-hidden',
                className
            )}
            {...rest}>
            <ProductTileCustomVerticalImageSection
                product={product}
                productUrl={productUrl}
                effectiveImgAspectRatio={effectiveImgAspectRatio}
                handleProductClick={handleProductClick}
                eatingList={[]}
            />

            <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-4 pr-2 pb-4">
                <div className="flex w-full min-w-0 flex-col gap-1">
                    <p className="body-copy-m m-0 w-full min-w-0 text-[var(--neutral-40)]">
                        {formattedDateLine || '\u00A0'}
                    </p>
                    <h3 className="body-copy-l-bold m-0 h-12 w-full min-w-0 line-clamp-2 text-left text-[var(--neutral-10)]">
                        <Link
                            to={productUrl}
                            className={cn(productCardHeadingLinkClass, 'after:rounded-3xl')}
                            onClick={() => handleProductClick?.(product)}>
                            {productName}
                        </Link>
                    </h3>
                </div>

                <div className="flex w-full min-w-0 flex-col gap-1" data-testid="product-tile-event-bar">
                    {priceBlock}
                    {categoryName ? (
                        <p className="body-copy-s-bold m-0 w-full min-w-0 text-[var(--neutral-40)]">{categoryName}</p>
                    ) : null}
                </div>
            </div>

            <span aria-hidden="true" className={productCardHeadingLinkFocusRingClass} />
        </div>
    );
}

const ProductTileCustomVerticalEventCard = forwardRef(ProductTileCustomVerticalEventCardInner);

export { ProductTileCustomVerticalEventCard };
